import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { useState } from 'react';
import type { ControlPlaneOnboardingView } from '../hooks/useControlPlaneOnboarding';

const useStyles = makeStyles(theme => ({
  section: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  command: {
    margin: 0,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.grey[900],
    color: theme.palette.common.white,
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  notice: {
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    padding: theme.spacing(2),
    background: theme.palette.background.default,
  },
}));

export function StatusPanel({ view }: { view: ControlPlaneOnboardingView }) {
  const classes = useStyles();
  const stalled =
    view.activity?.handoff?.alert?.code === 'HANDOFF_CONNECTION_STALLED';
  return (
    <Box className={classes.section} aria-labelledby="connection-status">
      <Box className={classes.row}>
        <Typography id="connection-status" variant="h6">
          Estado da conexão
        </Typography>
        {/*
          No state chip here by design. The canonical operational projection is
          the single owner of the connection state (ADR-009); this panel used to
          render a handoff-derived label beside it, which let the page show
          "Conectado" next to the projection's "Expirada". What survives is the
          pairing material and the concrete expiry instant below — facts, not a
          competing verdict.
        */}
      </Box>
      {view.session && (
        <Typography variant="body2">
          A credencial desta sessão vale até{' '}
          {new Date(view.session.expiresAt).toLocaleString('pt-BR')}.
        </Typography>
      )}
      {!view.session && view.pairing?.status === 'created' && (
        <Typography variant="body2">
          O código de pareamento vale até{' '}
          {new Date(view.pairing.expiresAt).toLocaleString('pt-BR')}.
        </Typography>
      )}
      {stalled && (
        <Typography color="error" role="alert">
          Conexão atrasada: a evidência foi aceita, mas o handoff ainda não foi
          concluído. Verifique a saúde do agente e tente novamente.
        </Typography>
      )}
    </Box>
  );
}

export function ConnectionSetupPanel(props: {
  busy: boolean;
  onGenerate(): void;
}) {
  const classes = useStyles();
  const [confirmed, setConfirmed] = useState(false);
  return (
    <Box className={classes.section}>
      <Box className={classes.notice}>
        <Typography variant="body2">
          O repositório será clonado em um workspace exclusivo desta atividade;
          novas tentativas reutilizam o mesmo workspace.
        </Typography>
        <Typography variant="body2">
          O código vale 10 minutos e é de uso único. A credencial é gerada no
          terminal, fica vinculada a esta atividade e vale até 20 horas.
        </Typography>
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={confirmed}
            onChange={event => setConfirmed(event.target.checked)}
            color="primary"
          />
        }
        label="Confirmo a criação do pareamento e entendo que o código será exibido uma única vez"
      />
      <Box>
        <Button
          color="primary"
          variant="contained"
          disabled={!confirmed || props.busy}
          onClick={() => props.onGenerate()}
        >
          {props.busy ? 'Gerando pareamento…' : 'Gerar código de pareamento'}
        </Button>
      </Box>
    </Box>
  );
}

export function PairingPanel({ view }: { view: ControlPlaneOnboardingView }) {
  const classes = useStyles();
  if (!view.pairing || view.session) return null;
  if (!view.pairingCommand) {
    return (
      <Typography>
        Este pareamento já existe, mas o código não pode ser reexibido. Gere um
        novo código para invalidar o anterior.
      </Typography>
    );
  }
  return (
    <Box className={classes.section}>
      <Typography variant="h6">Comando de pareamento</Typography>
      <Typography>
        Copie e execute no terminal. No Windows, use o Git Bash. A credencial
        definitiva será criada localmente e nunca passará por esta página.
      </Typography>
      <Typography>
        Código de uso único: <strong>{view.pairingCommand.pairingCode}</strong>
      </Typography>
      <pre className={classes.command} aria-label="Comando de pareamento">
        {view.pairingCommand.terminalCommand}
      </pre>
      <Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            void navigator.clipboard
              .writeText(view.pairingCommand?.terminalCommand ?? '')
              .then(() =>
                view.announce('Comando copiado para a área de transferência.'),
              )
              .catch(() => view.announce('Não foi possível copiar o script.'));
          }}
        >
          Copiar comando
        </Button>
      </Box>
    </Box>
  );
}
